import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { LOGO_URL } from "@/lib/constants";
import { Menu, X, Plus, Heart, LayoutDashboard, LogOut, ShieldCheck, Settings, Sparkles } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const linkCls = ({ isActive }) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive ? "text-emerald-900 bg-emerald-50" : "text-slate-700 hover:text-emerald-900"
    }`;

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/90 border-b border-slate-200/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-4 h-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <img src={LOGO_URL} alt="FLUORIT" className="h-10 w-10 object-contain rounded" />
          <div className="hidden sm:block leading-tight">
            <div className="text-[15px] font-extrabold text-emerald-950 tracking-tight">FLUORIT</div>
            <div className="text-[10px] text-slate-500 -mt-0.5">فلوريت للاستثمارات العقارية</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1 me-auto ms-6">
          <NavLink to="/" data-testid="nav-home" className={linkCls} end>{t.nav.home}</NavLink>
          <NavLink to="/properties" data-testid="nav-properties" className={linkCls}>{t.nav.properties}</NavLink>
          <NavLink to="/sell" data-testid="nav-add" className={linkCls}>{t.nav.add}</NavLink>
          {user && <NavLink to="/favorites" data-testid="nav-favs" className={linkCls}>{t.nav.favs}</NavLink>}
        </nav>

        <div className="ms-auto flex items-center gap-2">
          <Link
            to="/ai-property-assistant"
            data-testid="nav-ai-assistant"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold bg-[#EBDDA6] hover:bg-[#B7962E] text-[#062C22] rounded-full px-3 sm:px-4 py-1.5 sm:py-2 shadow-[0_10px_25px_-12px_rgba(183,150,46,.7)] transition"
          >
            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden md:inline">🤖 مساعد فلوريت العقاري</span>
            <span className="md:hidden">🤖 مساعد فلوريت</span>
          </Link>
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-300 hover:border-emerald-900 hover:text-emerald-900 transition"
          >
            {lang === "ar" ? "EN" : "عربي"}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="nav-account" variant="outline" size="sm" className="border-emerald-900/20">
                  {user.name?.split(" ")[0] || t.nav.account}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-slate-500 font-normal">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="w-4 h-4 me-2" /> {t.dash.title}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/favorites")}>
                  <Heart className="w-4 h-4 me-2" /> {t.nav.favs}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/sell")}>
                  <Plus className="w-4 h-4 me-2" /> {t.nav.add}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="w-4 h-4 me-2" /> {t.nav.admin}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] text-slate-400 font-normal">{t.nav.settings}</DropdownMenuLabel>
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-slate-500 text-xs">
                  <LogOut className="w-3.5 h-3.5 me-2" /> {t.nav.logout}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button data-testid="nav-login" variant="ghost" size="sm" onClick={() => nav("/login")}>{t.nav.login}</Button>
              <Button data-testid="nav-register" size="sm" className="bg-emerald-900 hover:bg-emerald-950" onClick={() => nav("/register")}>{t.nav.register}</Button>
            </>
          )}

          <button data-testid="nav-mobile-toggle" className="md:hidden p-2 rounded-md text-slate-700" onClick={() => setOpen(!open)}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link to="/ai-property-assistant" onClick={() => setOpen(false)} data-testid="nav-ai-assistant-mobile"
                  className="mb-2 inline-flex items-center gap-2 justify-center text-sm font-bold bg-[#EBDDA6] hover:bg-[#B7962E] text-[#062C22] rounded-full px-4 py-2">
              <Sparkles className="w-4 h-4" /> 🤖 مساعد فلوريت العقاري
            </Link>
            <NavLink to="/" onClick={() => setOpen(false)} className={linkCls} end>{t.nav.home}</NavLink>
            <NavLink to="/properties" onClick={() => setOpen(false)} className={linkCls}>{t.nav.properties}</NavLink>
            <NavLink to="/sell" onClick={() => setOpen(false)} className={linkCls}>{t.nav.add}</NavLink>
            {user && <NavLink to="/favorites" onClick={() => setOpen(false)} className={linkCls}>{t.nav.favs}</NavLink>}
            {user && <NavLink to="/dashboard" onClick={() => setOpen(false)} className={linkCls}>{t.dash.title}</NavLink>}
            {user?.role === "admin" && <NavLink to="/admin" onClick={() => setOpen(false)} className={linkCls}>{t.nav.admin}</NavLink>}
          </div>
        </div>
      )}
    </header>
  );
}
